/**
 * Store: Discount campaigns
 * -----------------------------------------------------------------------------
 * Active and promoted campaign states.
 * - Tied to the discount code application component.
 * - Also used to trigger query of discount code validity.
 * - Only JSDOC custom parameters, ignore module state or object.
 *
 */
import { defineStore } from 'pinia';
import { decodeApiId, transformId } from '~/helpers/graphql';
import { checkFreebies } from '~/helpers/freebies';
import {
  checkIncludedOrExcludedProducts,
  getCampaignCode,
  isDiscountApplicableForCode,
} from '~/helpers/discounts';
import variantIdQuery from '~graphql/nodes-by-id-product-inline.gql';
import { formatProduct } from '~/format/format-product';
import useCartStore from './cart';

const useDiscountCampaignsStore = defineStore('discount-campaigns', {
  state: () => ({
    active: false,
    enteredCode: '',
    codeState: 'ready',
    removeCode: '',
    urlDiscount: false,
    validNativeCode: '',
    cartItemPrices: [{}],
    errorType: '',
    promoted: false,
    thresholds: [],
    oldThresholds: [],
    freeProducts: [],
    initialCheck: false,
    freeShipping: false,
  }),

  getters: {
    /**
     * Get graduated discount tiers for a specific variant
     * @param {Number} variantId - Variant ID
     * @param {String} type - Type of campaign - `active` or `promoted`
     * @returns {Array} Array of {percentage, quantity} objects
     */
    getGraduatedDiscountTiers: (state) => {
      return (variantId, productId, type = 'active') => {
        const campaign = state[type];
        if (!campaign?.graduatedDiscounts || !variantId) {
          return [];
        }

        const graduated = campaign.graduatedDiscounts;

        // Find the first matching graduated discount configuration
        const matchingGraduated = graduated.find((graduatedItem) => {
          return checkIncludedOrExcludedProducts(graduatedItem, {
            productId: Number(productId),
            variantId: Number(variantId),
          });
        });

        // Return tiers only from the first matching configuration
        if (matchingGraduated?.tiers) {
          return matchingGraduated.tiers.map((tier) => ({
            percentage: tier.discountPercentage,
            quantity: tier.thresholdQuantity,
          }));
        }

        return [];
      };
    },

    /**
     * Get the graduated discount group index for a specific variant
     * Returns the index of the group that includes this variant, or -1 if not found
     * @param {Number} variantId - Variant ID
     * @param {String} type - Type of campaign - `active` or `promoted`
     * @returns {Number} Group index or -1
     */
    getGraduatedDiscountGroupIndex: (state) => {
      return (variantId, productId, type = 'active') => {
        const campaign = state[type];
        if (!campaign?.graduatedDiscounts || (!variantId && !productId)) {
          return -1;
        }

        return campaign.graduatedDiscounts.findIndex((graduatedItem) => {
          return checkIncludedOrExcludedProducts(graduatedItem, {
            productId: Number(productId),
            variantId: Number(variantId),
          });
        });
      };
    },

    /**
     * Check if variant is included in any graduated discount group
     * @param {Number} variantId - Variant ID
     * @param {String} type - Type of campaign - `active` or `promoted`
     * @returns {Boolean}
     */
    isVariantInGraduatedDiscount() {
      return (variantId, type = 'active') => {
        return this.getGraduatedDiscountGroupIndex(variantId, type) !== -1;
      };
    },

    getXforYProducts: (state) => {
      return (logic, type) => {
        if (
          !state[type] ||
          !logic ||
          (!logic.discountPreview && type === 'promoted') ||
          logic.onSale
        ) {
          return 0;
        }

        const XforYDiscounts = state[type].discounts.filter(
          (entry) => entry.calculationType === 'buy-x-get-y',
        );

        if (!XforYDiscounts) {
          return false;
        }

        const campaignCode = getCampaignCode();

        const discounts = XforYDiscounts.filter((entry) => {
          return (
            isDiscountApplicableForCode(entry, campaignCode) &&
            checkIncludedOrExcludedProducts(entry, logic)
          );
        });

        return discounts?.length ? discounts[0] : false;
      };
    },

    /**
     * Get best campaign discount for collection/product/variant ID.
     * - Update wishlist custom product cards logic too.
     * @param {Object} logic - Product and variant data.
     * @param {Boolean} logic.discountPreview - Show campaign preview.
     * @param {Number} logic.collectionIds - Collection IDs.
     * @param {Boolean} logic.onSale - Product has sale price.
     * @param {Number} logic.productId - Product ID.
     * @param {Number} logic.variantId - Variant ID.
     * @param {String} type - Type of campaign - `active` or `promoted`.
     * @returns {Number}
     */
    getCampaignDiscount: (state) => {
      return (logic, type) => {
        if (
          !state[type] ||
          !logic ||
          (!logic.discountPreview && type === 'promoted') ||
          logic.onSale
        ) {
          return null;
        }

        // create copy so we dont manipulate original array values
        const allDiscounts = [...state[type].discounts];
        const campaignCode = getCampaignCode();

        // First check for fixed discounts, this type beats all others
        const fixedDiscounts = allDiscounts
          .filter((entry) => {
            return (
              entry.calculationType === 'fixed' &&
              isDiscountApplicableForCode(entry, campaignCode) &&
              checkIncludedOrExcludedProducts(entry, logic)
            );
          })
          .map(
            (entry) =>
              entry.fixedPrice.find(
                (fixedPrice) =>
                  fixedPrice.marketHandle === cnvs.store.marketName,
              )?.amount,
          )
          .filter(Boolean)
          .sort((a, b) => b - a);

        /* return discount without comma to match the standard shopify
        / product price format simplifies many calculations
        */

        if (fixedDiscounts.length) {
          return {
            calculationType: 'fixed',
            amount: fixedDiscounts[0] * 100,
          };
        }

        let cartTotal;
        if (state[type].conditionalDiscounts.length) {
          cartTotal = useCartStore().getIncludedCartTotal;
        }

        for (const condDisc of state[type].conditionalDiscounts) {
          // condValue is only an array of {marketHandle, amount} for
          // 'minimumAmount' conditions - other condTypes (e.g.
          // 'minimumQuantity') store a plain number, so skip those here.
          if (condDisc.condType !== 'minimumAmount') {
            continue;
          }

          const currentMarket = cnvs.store.marketName;
          const matchingDiscount = condDisc.condValue?.find(
            (discount) => discount.marketHandle === currentMarket,
          );
          const threshold = matchingDiscount
            ? matchingDiscount.amount * 100
            : null;

          if (
            condDisc.condValue &&
            isDiscountApplicableForCode(condDisc, campaignCode) &&
            Object.keys(condDisc.condValue).length &&
            threshold !== null &&
            cartTotal >= threshold
          ) {
            const newDisc = {
              calculationType: 'perc',
              discount: condDisc.discPercentage,
              exclude: {
                collectionIds: [],
                productIds: [],
                variantIds: [],
              },
              include: {},
            };

            const keyMap = {
              include: {
                type: 'discSelectType',
                ids: 'discInclIds',
              },
              exclude: {
                type: 'discExclType',
                ids: 'discExclIds',
              },
            };

            // populate 'include' and 'exclude' properties of discount
            ['include', 'exclude'].forEach((filterType) => {
              const typeKey = condDisc[keyMap[filterType].type];
              const ids = [...condDisc[keyMap[filterType].ids]];
              /**
               * Currently, when conditional discounts are set up on "products"
               * basis, we are writing all these "product IDs" into both
               * "productIds" and "variantIds". This is because we cannot
               * differentiate between variants and products at the moment.
               */
              ['collectionIds', 'productIds', 'variantIds'].forEach((key) => {
                newDisc[filterType][key] =
                  (key === 'collectionIds' && typeKey === 'collections') ||
                  (key !== 'collectionIds' && typeKey === 'products')
                    ? ids
                    : [];
              });
            });

            allDiscounts.push(newDisc);
          }
        }

        const discounts = allDiscounts
          .filter((entry) => {
            return (
              entry.calculationType === 'perc' &&
              checkIncludedOrExcludedProducts(entry, logic) &&
              isDiscountApplicableForCode(entry, campaignCode)
            );
          })
          .filter(Boolean)
          .map((entry) => entry.discount)
          .sort((a, b) => b - a);

        return discounts?.length
          ? {
              calculationType: 'perc',
              amount: discounts[0],
            }
          : {
              calculationType: 'perc',
              amount: 0,
            };
      };
    },

    /**
     * Get campaign thresholds for free shipping and freebies.
     * - Also determines if threshold is active.
     * @param {String} type - Type of campaign - `active` or `promoted`.
     * @returns {Array<Object>}
     */
    getCampaignThresholds: (state) => {
      return (type) => {
        const cartStore = useCartStore();
        const cartTotal = cartStore.getIncludedCartTotal;
        const cartItems = cartStore.getIncludedCartItems;

        let freeProducts = [];
        let freebieQuantity = 0;

        /**
         * Early return empty list if no campaign is active.
         */
        if (!state[type]?.freeProducts?.length) {
          return [];
        }

        const campaignCode = getCampaignCode().toLowerCase();

        const productBased = checkFreebies(
          state[type]?.freeProducts,
          'product',
          cartItems,
          campaignCode,
        );

        const thresholdBased = checkFreebies(
          state[type]?.freeProducts,
          'threshold',
          cartItems,
          campaignCode,
        );

        if (productBased.length > 0) {
          freeProducts = freeProducts.concat(productBased);
        }

        if (thresholdBased.length > 0) {
          freeProducts = freeProducts.concat(thresholdBased);
        }

        freebieQuantity = freeProducts.length;

        /**
         * Calculate free thresholds and whether they're active.
         */
        const freebieThresholds = freeProducts
          .map((freebie) => {
            let activeState,
              allowHiddenVariants,
              autoAdd,
              freebieType,
              remaining,
              threshold;
            /**
             * We need to adjust the value according to the freebie type
             */
            if (freebie.type === 'product') {
              activeState = true;
              autoAdd = Boolean(freebie.freeProductsAutoInsertActive);
              remaining = 0;
              threshold = 0;
              freebieType = 'product';
              freebieQuantity = 1;
              allowHiddenVariants = Boolean(freebie.allowHiddenVariants);
            } else {
              threshold = freebie.thresholds.find((thresholdObject) => {
                return thresholdObject.marketHandle === cnvs.store.marketName;
              });

              if (!threshold) {
                return false;
              }

              threshold = threshold.threshold * 100;
              activeState = cartTotal >= threshold;
              autoAdd = Boolean(freebie.freeProductsAutoInsertActive);
              freebieQuantity = freebie.quantity;
              remaining = threshold - cartTotal;
              freebieType = 'threshold';
              allowHiddenVariants = Boolean(freebie.allowHiddenVariants);
            }

            const groupUid = freebie.uid;
            const code = campaignCode;
            const rawCampaignId = state[type].campaignId;
            /**
             * if the freebie is not influencer based, it's free for all so we don't want an
             * INFL at the start or a code connected to it
             */
            const campaignIdClean = rawCampaignId.replace('INFL_', '');

            let handle;
            /** we need the code if there is a threshold that is influencer specific */
            if (freebieType === 'threshold') {
              handle =
                freebie.applyTo === 'allCodes'
                  ? `${campaignIdClean}-${freebieType}-freebie-${freebie.groupId}-all-${threshold}`
                  : `${rawCampaignId}-${freebieType}-freebie-${freebie.groupId}-${code}-${threshold}`;
            }

            /**
             * If the freebie is product-based, we need to add the condition IDs
             */
            if (freebieType === 'product') {
              handle =
                freebie.applyTo === 'allCodes'
                  ? `${campaignIdClean}-${freebieType}-freebie-${freebie.groupId}-all-${freebie.cartItemId}`
                  : `${rawCampaignId}-${freebieType}-freebie-${freebie.groupId}-${code}-${freebie.cartItemId}`;
            }

            return {
              active: activeState,
              autoAdd,
              handle,
              groupUid,
              quantity: freebieQuantity,
              freeProducts: [],
              remaining,
              threshold,
              type: freebieType,
              variantIds: freebie.variantIds,
              allowHiddenVariants,
              cartVariantSelectorActive: Boolean(
                freebie.cartVariantSelectorActive,
              ),
            };
          })
          .filter(Boolean);

        /**
         * Combine and sort by threshold ascending.
         */
        const thresholds = freebieThresholds.toSorted(
          (a, b) => a.threshold - b.threshold,
        );

        return thresholds;
      };
    },

    /**
     * Go through already saved cart item prices and return current discounted price.
     * @param {Number} variantId - Variant ID.
     * @param {Number} price - Current price.
     * @returns {Number}
     */
    getCurrentItemDiscountedPrice: (state) => {
      return (variantId, price) => {
        const newItem = state.cartItemPrices.find(
          (item) => item.variant_id === variantId,
        );
        return newItem ? newItem.discounted_price : price;
      };
    },

    /**
     * Get the first available conditional discount. We use only the first match
     * as this is what the checkout calculates.
     * @param {Array<Number>} - Array of numbers with IDs relating to a product:
     * productId, variantIds and collectionIds.
     * @param {String} type - Type of campaign - `active` or `promoted`.
     */
    getConditionalDiscount: (state) => {
      return (ids, type) => {
        return state[type].conditionalDiscounts?.find((condDisc) => {
          const included = condDisc.discInclIds.some((id) => {
            const idNum = Number(id);
            return ids.includes(idNum);
          });
          const excluded = condDisc.discExclIds.some((id) => {
            const idNum = Number(id);
            return ids.includes(idNum);
          });
          return included && !excluded;
        });
      };
    },
  },

  actions: {
    /**
     * Commit campaign.
     * @param {Object} data - Data object to set.
     * @param {Object} campaign - Campaign to set.
     * @param {String} type - Type of campaign - `active` or `promoted`.
     */
    setCampaign({ campaign, type }) {
      this[type] = campaign;
      if (type === 'active' && !campaign) {
        this.thresholds = [];
      }
    },

    /**
     * Set free shipping state whenever a code is applied for it.
     * @param {Boolean} state - Free shipping state.
     * - Used to determine if free shipping is active.
     */
    setFreeShipping(state) {
      this.freeShipping = state;
    },

    /**
     * Used for the inital code check to make sure codeState is reset
     * @param {Boolean} state
     */
    setInitialCheck(state) {
      this.initialCheck = state;
    },

    /**
     * To pass along for clearing the code
     * @param {String} clearCode
     */
    setRemoveCode(code, state) {
      this.removeCode = {
        code,
        state,
      };
    },

    refreshCampaignThresholds() {
      this.oldThresholds = this.thresholds;

      if (this.active) {
        const newThresholds = this.getCampaignThresholds('active');

        this.thresholds = newThresholds.map((threshold) => {
          if (threshold.variantIds) {
            const freeProducts = this.freeProducts.filter((product) => {
              return threshold.variantIds.includes(
                product.selectedOrFirstAvailableVariant.id.toString(),
              );
            });

            return { ...threshold, freeProducts };
          }

          return threshold;
        });
      } else {
        this.thresholds = [];
      }
    },

    setValidNativeCode(code) {
      this.validNativeCode = code;
    },

    /**
     * Commit discount code query to check validity.
     * @param {String} code - Discount code to query, always uppercase.
     * - cleared after code has been validated and applied.
     */
    setEnteredCode(code) {
      this.enteredCode = code;
    },

    /**
     * Commit discount code query.
     * @param {String} code - Discount code to query.
     */
    setInitialCode(initialCode) {
      if (!initialCode) {
        return;
      }

      this.setEnteredCode(initialCode.toUpperCase());
      this.setUrlDiscount(true);
    },

    /**
     * Commit discount code query state.
     * @param {String} codeState - Discount code query state.
     */
    setCodeState(codeState) {
      this.codeState = codeState;
    },

    /**
     * Commit thresholds for the active campaign.
     * @param {Object} thresholds - Campaign thresholds
     */
    setCampaignThresholds(thresholds) {
      this.thresholds = thresholds;
    },
    /**
     * Set cart item prices state.
     * @param {Object} CartItemPrices - Prices for the items in the cart.
     */
    setCartItemPrices(cartItemPrices) {
      this.cartItemPrices = cartItemPrices;
    },

    /**
     * Commit discount code error type.
     * @param {String} errorType - Discount code error type.
     */
    setErrorType(errorType) {
      if (!errorType) {
        this.errorType = '';
        return;
      }

      this.errorType = errorType;
    },

    /**
     * Commit flag if discount derives from url apply.
     * This is set to true if the discount code from cart has changed on initial load.
     * @param {Boolean} urlDiscount - Flag if discount derives from url apply.
     */
    setUrlDiscount(urlDiscount) {
      this.urlDiscount = urlDiscount;
    },

    async loadFreeProducts() {
      let variantQueryIds = [];
      this.thresholds.forEach((threshold) => {
        if (threshold.variantIds) {
          variantQueryIds = variantQueryIds.concat(
            ...threshold.variantIds.map((id) =>
              transformId(id, 'ProductVariant'),
            ),
          );
        }
      });
      variantQueryIds = [...new Set(variantQueryIds)];

      const CHUNK_SIZE = 25;
      const chunks = [];
      for (let index = 0; index < variantQueryIds.length; index += CHUNK_SIZE) {
        chunks.push(variantQueryIds.slice(index, index + CHUNK_SIZE));
      }

      const responses = await Promise.all(
        chunks.map((ids) =>
          cnvs.Query({
            query: variantIdQuery,
            variables: {
              country: cnvs.store.country,
              ids,
              language: cnvs.store.language,
              limit: cnvs.discount.freebies.limit,
            },
          }),
        ),
      );

      const allNodes = responses.flatMap((response) => response?.nodes);

      const formattedProducts = allNodes
        .map((node) => {
          if (!node) {
            return false;
          }

          const variantId = decodeApiId(node.id);
          return { ...formatProduct(node.product, { variantId }) };
        })
        .filter(Boolean);

      this.freeProducts = formattedProducts;

      const newThresholds = this.thresholds.map((threshold) => {
        if (threshold.variantIds) {
          const freeProducts = formattedProducts
            .filter((product) => {
              return threshold.variantIds.includes(
                product.selectedOrFirstAvailableVariant.id.toString(),
              );
            })
            .map((product) => ({
              ...product,
              allowHiddenVariants: threshold.allowHiddenVariants,
            }));

          return { ...threshold, freeProducts };
        }

        return threshold;
      });

      this.thresholds = newThresholds;
    },
  },
  persist: true,
});

export default useDiscountCampaignsStore;
